/**
 * Remember-me storage tier.
 *
 * "Remember me" is, at heart, one decision: should credentials survive the
 * browser being closed? We answer it with the storage TIER rather than a timer:
 *
 *   - Remember ON  → localStorage   (survives browser restart - persistent)
 *   - Remember OFF → sessionStorage  (the browser itself wipes it on close)
 *
 * sessionStorage is the cleanest way to get true "log me out when I close the
 * browser" without trusting the client to run cleanup code - there is no timer
 * to miss and nothing left behind on disk. It survives a same-tab refresh (F5)
 * but not a tab/browser close, which is exactly the expected feel of an
 * un-remembered session.
 *
 * One flag (`resumark-remember`, kept in localStorage so it outlives a
 * session-only blob) drives the routing for BOTH the zustand auth store and the
 * supabase-js client, so a single user choice governs every persisted
 * credential. The access-token / refresh-token LIFETIMES are unchanged - a
 * remembered session is the sliding remember-window (see REMEMBER_WINDOW_HOURS in
 * stores/auth.ts); this only decides whether it may outlive the browser.
 */

const REMEMBER_KEY = 'resumark-remember';
// The PENDING choice lives in sessionStorage: it survives the OAuth redirect
// chain (same tab) but the browser wipes it the moment the tab/window closes.
// That is exactly the lifetime we want for an *un-consummated* opt-in - toggling
// "keep me signed in" ON and then leaving WITHOUT signing in must never persist.
const PENDING_KEY = 'resumark-remember-pending';

/**
 * Read the EFFECTIVE remember choice. Defaults to OFF - "keep me signed in" is
 * OPT-IN.
 *
 * Precedence:
 *   1. A PENDING choice (staged when the user clicks a sign-in provider) wins
 *      while a sign-in is in flight, so the session tokens are routed to the
 *      tier the user just chose even before the choice is made durable.
 *   2. Otherwise the DURABLE flag - the choice committed by the LAST successful
 *      sign-in - wins.
 *
 * Persisting credentials past the browser session is an extra, optional
 * convenience the user must actively choose; we never pre-select it. A
 * pre-ticked default is not a "clear affirmative action" and so isn't valid
 * consent under the DPDP Act (and GDPR) - and OWASP likewise says persistent
 * login should be opt-in and off by default. So only an explicit '1' remembers;
 * anything else - including never-set - is session-only.
 */
export function getRemember(): boolean {
  try {
    const pending = sessionStorage.getItem(PENDING_KEY);
    if (pending === '1') return true;
    if (pending === '0') return false;
  } catch {
    /* sessionStorage blocked - fall through to the durable flag */
  }
  try {
    return localStorage.getItem(REMEMBER_KEY) === '1';
  } catch {
    return false; // storage blocked (private mode) → session-only, never remembered
  }
}

/** Persist the DURABLE choice. Written to localStorage so it survives even when
 *  the session blob it governs lives in (volatile) sessionStorage. This is only
 *  ever written via commitPendingRemember, i.e. at actual sign-in success. */
export function setRemember(remember: boolean): void {
  try {
    localStorage.setItem(REMEMBER_KEY, remember ? '1' : '0');
  } catch {
    /* storage blocked - nothing to persist */
  }
}

/**
 * Stage the choice for an in-flight sign-in. Kept in sessionStorage so it
 * survives the OAuth redirect (same tab) but is discarded if the user closes the
 * tab without completing sign-in - so an opt-in that never leads to a sign-in
 * never becomes durable.
 */
export function setPendingRemember(remember: boolean): void {
  try {
    sessionStorage.setItem(PENDING_KEY, remember ? '1' : '0');
  } catch {
    /* sessionStorage blocked - nothing to stage */
  }
}

/** The staged choice, or null when none is in flight. */
export function getPendingRemember(): boolean | null {
  try {
    const p = sessionStorage.getItem(PENDING_KEY);
    if (p === '1') return true;
    if (p === '0') return false;
  } catch {
    /* ignore */
  }
  return null;
}

/** Discard any staged choice - called when the user lands (back) on the login
 *  screen, so an abandoned earlier attempt can't leak into a fresh one. */
export function clearPendingRemember(): void {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Commit the staged choice to the durable flag at sign-in success, then clear
 * the staging. Returns the effective remember value. If nothing was staged, the
 * existing durable choice stands. THIS is the ONLY place a toggle becomes
 * persistent - so "keep me signed in" only ever sticks when a sign-in actually
 * completes.
 */
export function commitPendingRemember(): boolean {
  const pending = getPendingRemember();
  if (pending !== null) {
    setRemember(pending);
    clearPendingRemember();
    return pending;
  }
  return getRemember();
}

function safeLocal(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function safeSession(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * A `Storage`-shaped adapter (getItem / setItem / removeItem) that reads and
 * writes whichever tier the current remember flag selects, and keeps the OTHER
 * tier clear so a key only ever lives in one place at a time.
 *
 * Reads fall back to the inactive tier when the active one misses, which covers
 * two real cases without any migration step:
 *   1. An existing user who was signed in BEFORE this adapter shipped (their
 *      session sits in localStorage) - it's found and kept working.
 *   2. A user who flips remember OFF mid-session - the very next write moves the
 *      blob to sessionStorage and removes the localStorage copy, so the flip
 *      takes effect immediately rather than on next login.
 *
 * Satisfies both zustand's `StateStorage` and supabase-js's storage interface.
 */
export function createRememberStorage(): {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
} {
  return {
    getItem(key: string): string | null {
      const remember = getRemember();
      const active = remember ? safeLocal() : safeSession();
      const inactive = remember ? safeSession() : safeLocal();
      const hit = active?.getItem(key) ?? null;
      if (hit !== null) return hit;
      // Fallback: pick up a value the other tier still holds (pre-adapter
      // session, or a not-yet-migrated flip) so we never spuriously log out.
      return inactive?.getItem(key) ?? null;
    },

    setItem(key: string, value: string): void {
      const remember = getRemember();
      const active = remember ? safeLocal() : safeSession();
      const inactive = remember ? safeSession() : safeLocal();
      try {
        active?.setItem(key, value);
      } catch {
        /* quota / blocked - best effort */
      }
      // Ensure exactly one tier holds the key.
      try {
        inactive?.removeItem(key);
      } catch {
        /* ignore */
      }
    },

    removeItem(key: string): void {
      try {
        safeLocal()?.removeItem(key);
      } catch {
        /* ignore */
      }
      try {
        safeSession()?.removeItem(key);
      } catch {
        /* ignore */
      }
    },
  };
}
